import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { Tags, Pencil, Check, Plus, Trash2, Variable, ArrowRight, Brain, History, MessageSquare } from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

// ─── Collapsed View ───────────────────────────────────
const CollapsedView = ({ data, onEdit, labels }) => {
  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#EDE9FE", padding: "10px", minWidth: "260px", maxWidth: "320px" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit intent classifier"
      >
        <Pencil size={12} className="text-violet-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-violet-100 rounded-lg">
            <Tags size={14} className="text-violet-600" />
          </div>
          <span className="text-[12px] font-bold text-violet-900">AI Intent Classifier</span>
        </div>

        {labels.length > 0 ? (
          <div className="flex flex-wrap gap-1 mt-1">
            {labels.map((lbl) => (
              <div key={lbl.id} className="flex items-center gap-1">
                <span className="text-[10px] bg-violet-100 text-violet-800 px-2 py-0.5 rounded-full font-medium">
                  {lbl.value}
                </span>
                <span className={`w-1.5 h-1.5 rounded-full ${lbl.connectedNodeId ? "bg-green-500" : "bg-gray-300"}`} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[10px] text-gray-400 italic">No labels defined</p>
        )}

        {data.prompt && (
          <div className="flex items-start gap-1.5 mt-1.5">
            <Brain size={10} className="text-violet-400 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-gray-600 leading-tight">
              {data.prompt.length > 80 ? data.prompt.substring(0, 80) + "..." : data.prompt}
            </p>
          </div>
        )}

        {/* History depth indicator */}
        {(data.historyDepth && data.historyDepth > 1) && (
          <div className="flex items-center gap-1 mt-1.5 text-[9px] text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
            <History size={8} />
            <span className="font-medium">
              Last {data.historyDepth} {data.historyMode === 'user_and_bot' ? 'messages' : 'user messages'}
            </span>
          </div>
        )}

        {data.fallbackVariable && (
          <div className="flex items-center gap-1 mt-1.5 text-[9px] text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
            <Variable size={8} />
            <span className="font-mono font-semibold">{`{{${data.fallbackVariable}}}`}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Expanded Editor ──────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [labels, setLabels] = useState(
    data.labels ? data.labels.map((l) => ({ ...l })) : []
  );
  const [fallbackVariable, setFallbackVariable] = useState(data.fallbackVariable || "");
  const [prompt, setPrompt] = useState(data.prompt || "");
  const [historyDepth, setHistoryDepth] = useState(data.historyDepth || 1);
  const [historyMode, setHistoryMode] = useState(data.historyMode || "user_only");
  const [newLabel, setNewLabel] = useState("");
  const promptRef = useRef(null);

  const insertVariable = (varName) => {
    if (!varName) return;
    const placeholder = `{{${varName}}}`;
    if (promptRef.current) {
      const pos = promptRef.current.selectionStart ?? prompt.length;
      const before = prompt.slice(0, pos);
      const after = prompt.slice(pos);
      setPrompt(before + placeholder + after);
    } else {
      setPrompt((prev) => prev + placeholder);
    }
  };

  const addLabel = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    if (labels.some((l) => l.value === trimmed)) return;
    setLabels([
      ...labels,
      { id: `lbl_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`, value: trimmed, connectedNodeId: null },
    ]);
    setNewLabel("");
  };

  const removeLabel = (id) => {
    setLabels(labels.filter((l) => l.id !== id));
  };

  const updateLabelValue = (id, value) => {
    setLabels(labels.map((l) => l.id === id ? { ...l, value } : l));
  };

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#EDE9FE", borderRadius: "12px", padding: "14px", minWidth: "320px", maxWidth: "400px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-violet-200 rounded-lg">
            <Tags size={14} className="text-violet-700" />
          </div>
          <span className="text-[13px] font-bold text-violet-900">AI Intent Classifier</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => onSave({ labels, fallbackVariable, prompt, historyDepth, historyMode })}
            className="px-3 py-1.5 text-[11px] font-bold bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} /> Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      <NodeEditorTabs nodeType="aiIntentClassifier">
      <div className="bg-white rounded-lg shadow-sm p-3 space-y-3">
        {/* Labels */}
        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">Intent Labels</label>
          {labels.map((lbl) => (
            <div key={lbl.id} className="mb-2">
              <div className="flex items-center gap-1.5 mb-1">
                <input
                  type="text"
                  value={lbl.value}
                  onChange={(e) => updateLabelValue(lbl.id, e.target.value)}
                  className="flex-1 px-2 py-1.5 text-[11px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-violet-400 placeholder:text-gray-400"
                  placeholder="e.g. order_status"
                />
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${lbl.connectedNodeId ? "bg-green-500" : "bg-gray-300"}`}
                  title={lbl.connectedNodeId ? "Connected" : "Not connected"}
                />
                <button onClick={() => removeLabel(lbl.id)} className="p-0.5 text-red-400 hover:text-red-600">
                  <Trash2 size={10} />
                </button>
              </div>
              {/* Connection indicator */}
              {lbl.connectedNodeId && (
                <div className="flex items-center gap-1.5 mt-1 px-2 py-1 bg-violet-50 rounded-lg">
                  <ArrowRight size={10} className="text-violet-600" />
                  <span className="text-[9px] text-violet-600 font-semibold">
                    Connected to node
                  </span>
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-1 mt-1">
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addLabel()}
              placeholder="New label..."
              className="flex-1 px-2 py-1.5 text-[11px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-violet-400 placeholder:text-gray-400"
            />
            <button
              onClick={addLabel}
              disabled={!newLabel.trim()}
              className="px-2 py-1.5 text-[10px] font-bold bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-30 flex items-center gap-0.5"
            >
              <Plus size={10} /> Add
            </button>
          </div>
        </div>

        {/* Classifier Prompt */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider">
              <Brain size={10} /> Classifier Prompt
            </label>
            <VariableDropdown
              value=""
              onChange={(name) => insertVariable(name)}
              flowVariables={flowVariables}
              placeholder="Insert variable"
              className="w-36"
            />
          </div>
          <textarea
            ref={promptRef}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-[12px] text-gray-800 bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-violet-400 resize-none placeholder:text-gray-400"
            placeholder="Explain to the AI how to map user messages to the defined intents (labels)."
          />
          <p className="text-[9px] text-gray-400 mt-0.5">
            Use {"{{variable_name}}"} to reference flow variables (e.g. last_user_message, phone_number).
          </p>
        </div>

        {/* Store intent in variable */}
        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1">
            <Variable size={10} /> Store intent in variable (optional)
          </label>
          <VariableDropdown
            value={fallbackVariable}
            onChange={(name) => setFallbackVariable(name)}
            flowVariables={flowVariables}
            allowCreate
            onCreateVariable={onCreateVariable}
            placeholder="Select variable..."
          />
        </div>

        {/* Message History Context */}
        <div className="p-2.5 rounded-lg bg-violet-50/50 border border-violet-100 space-y-2.5">
          <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            <History size={10} /> Message History Context
          </label>

          {/* History Depth */}
          <div>
            <label className="text-[10px] font-medium text-gray-600 mb-1 block">
              Check last how many messages
            </label>
            <input
              type="number"
              min={1}
              max={50}
              value={historyDepth}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setHistoryDepth(isNaN(val) || val < 1 ? 1 : val);
              }}
              className="w-full px-2.5 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-lg focus:ring-1 focus:ring-violet-400 focus:border-violet-400 focus:outline-none"
              placeholder="1"
            />
            <p className="text-[9px] text-gray-400 mt-0.5">
              Number of recent messages to include as context for classification.
            </p>
          </div>

          {/* History Mode */}
          <div>
            <label className="text-[10px] font-medium text-gray-600 mb-1 block">
              Include messages from
            </label>
            <div className="flex gap-1.5">
              <button
                onClick={() => setHistoryMode("user_only")}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-[10px] font-bold transition-colors border ${
                  historyMode === "user_only"
                    ? "bg-violet-50 border-violet-300 text-violet-700"
                    : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                <MessageSquare size={10} /> User Only
              </button>
              <button
                onClick={() => setHistoryMode("user_and_bot")}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg text-[10px] font-bold transition-colors border ${
                  historyMode === "user_and_bot"
                    ? "bg-violet-50 border-violet-300 text-violet-700"
                    : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                <MessageSquare size={10} /> User + Bot
              </button>
            </div>
            <p className="text-[9px] text-gray-400 mt-0.5">
              Choose whether to include only user messages or the full conversation for better context.
            </p>
          </div>
        </div>
      </div>

      </NodeEditorTabs>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => onSave({ labels, fallbackVariable, prompt, historyDepth, historyMode })}
          className="flex-1 py-2 text-[12px] font-bold bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} /> Save
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main AIIntentClassifierNode ──────────────────────
const AIIntentClassifierNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("aiIntentClassifier");

  const labels = data.labels || [];

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-violet-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      <CollapsedView data={data} onEdit={openEditor} labels={labels} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={() => closeEditor()}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
         presentation="fullscreen" onCancel={closeEditor} />
      </FlowNodeEditorShell>

      {/* Dynamic source handles – one per label + Other */}
      {labels.map((lbl, index) => (
        <Handle
          key={`intent_${lbl.id}`}
          type="source"
          position={Position.Right}
          id={`intent_${lbl.id}`}
          className={`!w-3 !h-3 !border-2 !border-white !shadow-sm ${
            lbl.connectedNodeId ? "!bg-green-500" : "!bg-violet-500"
          }`}
          style={{
            right: -6,
            top: `${((index + 1) / (labels.length + 2)) * 100}%`,
          }}
          title={lbl.value}
        />
      ))}

      {/* "Other" fallback handle – shown when there are labels */}
      {labels.length > 0 && (
        <Handle
          key="intent_other"
          type="source"
          position={Position.Right}
          id="intent_other"
          className="!w-3 !h-3 !border-2 !border-white !shadow-sm !bg-orange-500"
          style={{
            right: -6,
            top: `${((labels.length + 1) / (labels.length + 2)) * 100}%`,
          }}
          title="Other (no label match)"
        />
      )}

      {/* Intent labels + Other caption beside node for connection handles */}
      {labels.length > 0 && (
        <div className="absolute -right-16 top-0 h-full flex flex-col justify-around items-start py-4 pointer-events-none">
          {labels.map((lbl) => (
            <span
              key={lbl.id}
              className="text-[7px] text-violet-700 font-semibold truncate max-w-[60px] text-center"
              title={lbl.value}
            >
              {lbl.value || lbl.id}
            </span>
          ))}
          <span
            key="other_label"
            className="text-[7px] text-orange-600 font-semibold truncate max-w-[60px] text-center"
            title="Other (no label match)"
          >
            Other
          </span>
        </div>
      )}

      {/* Fallback handle if no labels */}
      {labels.length === 0 && (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-3.5 !h-3.5 !bg-violet-500 !border-2 !border-white !shadow-sm"
          style={{ right: -7 }}
        />
      )}
    </div>
  );
};

export default AIIntentClassifierNode;
